/** Role / DO’s / DON’Ts shown after slot pick for shift bookings (12h day/night, 24h live-in). */

export const SHIFT_GUIDELINE_SEARCH_SHIFTS = new Set(["12_day", "12_night", "24h"]);

export function isHourlySearchShift(searchShift) {
  return String(searchShift || "").toLowerCase() === "hourly";
}

export function shouldShowHourlyServicePackages({ serviceRole, searchShift }) {
  const role = String(serviceRole || "").toLowerCase();
  if (!(role === "attendant" || role === "nurses" || role === "nurse")) return false;
  const shift = String(searchShift || "").toLowerCase();
  // No shift context (e.g. deep-link hire): keep package/procedure picker.
  if (!shift) return true;
  return shift === "hourly";
}

/**
 * Caregiver Roles & Responsibilities is removed from the customer booking flow.
 * Digital Agreement + PRV run after caregiver accept (duty deployment on dashboard).
 * Keep guideline content available for caregiver-facing surfaces if needed later.
 */
export function shouldShowCareRoleGuidelines() {
  return false;
}

export function resolveShiftBookingSelection({ serviceRole, searchShift }) {
  const role = String(serviceRole || "").toLowerCase();
  const shift = String(searchShift || "").toLowerCase();
  const isNurse = role === "nurses" || role === "nurse";
  const roleKey = isNurse ? "nurses" : "attendant";

  if (shift === "12_day") {
    return {
      serviceRole: roleKey,
      serviceId: isNurse ? "nurse_12h_day" : "attendant_12h_day",
      serviceLabel: isNurse ? "Nurse — 12 hour day shift" : "Care Attendant — 12 hour day shift",
      durationMinutes: 12 * 60,
      caregiverFeeInr: null,
    };
  }
  if (shift === "12_night") {
    return {
      serviceRole: roleKey,
      serviceId: isNurse ? "nurse_12h_night" : "attendant_12h_night",
      serviceLabel: isNurse ? "Nurse — 12 hour night shift" : "Care Attendant — 12 hour night shift",
      durationMinutes: 12 * 60,
      caregiverFeeInr: null,
    };
  }
  if (shift === "24h") {
    return {
      serviceRole: roleKey,
      serviceId: isNurse ? "nurse_24h" : "attendant_24h",
      serviceLabel: isNurse ? "Nurse — 24 hours live in" : "Care Attendant — 24 hours live in",
      durationMinutes: 24 * 60,
      caregiverFeeInr: null,
    };
  }
  return null;
}

export const CARE_ROLE_GUIDELINES = {
  attendant: {
    id: "attendant",
    title: "Care Attendant",
    subtitle: "Role, Responsibilities, DO’s & DON’Ts",
    instruction: "Please read carefully and follow the guidelines to provide the best care.",
    accent: "attendant",
    overview: [
      {
        id: "adl",
        label:
          "Provide assistance with Activities of Daily Living (ADLs): bathing, grooming, dressing, toileting, feeding, mobility and companionship.",
        icon: "bath",
      },
      {
        id: "comfort",
        label: "Support patient comfort and safety at home.",
        icon: "shield",
      },
      {
        id: "transfer",
        label: "Assist with transfers and walking using safe techniques.",
        icon: "walk",
      },
      {
        id: "observe",
        label:
          "Observe and report changes in the patient's condition to the family and/or assigned healthcare professional.",
        icon: "eye",
      },
      {
        id: "clean",
        label: "Maintain cleanliness of the patient's immediate environment.",
        icon: "clean",
      },
      {
        id: "support",
        label: "Provide emotional support and companionship.",
        icon: "heart",
      },
      {
        id: "meds",
        label:
          "Assist with medicine reminders only when instructed by the family or nurse (not administer medicines unless legally authorized).",
        icon: "pill",
      },
    ],
    dos: [
      "Arrive on time and be prepared for duty.",
      "Maintain personal hygiene and a professional appearance.",
      "Treat the patient and family with dignity and respect.",
      "Follow the care plan and family instructions carefully.",
      "Wash hands frequently and follow infection-prevention practices.",
      "Report emergencies and sudden changes immediately.",
      "Keep patient information confidential.",
      "Record observations clearly when asked by the family or nurse.",
      "Seek help if you are unsure about any task.",
    ],
    donts: [
      "Do not administer injections / IVs (unless you are legally qualified and authorized).",
      "Do not diagnose medical conditions or prescribe treatment.",
      "Do not use abusive language or rough handling.",
      "Do not leave the patient unattended when continuous care is required.",
      "Do not accept cash, gifts, or personal favors from the patient/family.",
      "Do not share photos or patient details on social media.",
      "Do not smoke, drink, or use intoxicants on duty.",
      "Do not perform procedures outside your competency.",
    ],
    checkboxLabel: "I have read and understood my role, responsibilities, DO’s & DON’Ts.",
    continueLabel: "Agree & Continue",
  },
  nurses: {
    id: "nurses",
    title: "Nurse",
    subtitle: "Role, Responsibilities, DO’s & DON’Ts",
    instruction: "Please read carefully and follow the guidelines to provide the best care.",
    accent: "nurse",
    overview: [
      {
        id: "care",
        label: "Provide nursing care as prescribed by the treating physician.",
        icon: "syringe",
      },
      {
        id: "meds",
        label: "Administer medicines and injections as legally permitted.",
        icon: "pill",
      },
      {
        id: "vitals",
        label: "Monitor vital signs and maintain nursing records.",
        icon: "clipboard",
      },
      {
        id: "wound",
        label:
          "Perform wound dressing, catheter care, Ryle's tube care and other skilled procedures within competence.",
        icon: "bandage",
      },
      {
        id: "educate",
        label: "Educate patient and family on home care.",
        icon: "people",
      },
      {
        id: "warn",
        label: "Identify early warning signs and escalate medical concerns.",
        icon: "alert",
      },
      {
        id: "coord",
        label: "Coordinate with doctors and other healthcare providers.",
        icon: "stethoscope",
      },
    ],
    dos: [
      "Verify doctor’s instructions before performing any nursing procedure.",
      "Maintain accurate documentation of vitals, medicines, and procedures.",
      "Follow infection-prevention and aseptic techniques at all times.",
      "Verify patient identity before administering any medication or procedure.",
      "Report adverse reactions or deterioration immediately to the doctor/family.",
      "Maintain valid nursing registration / license as required.",
      "Respect patient privacy and dignity during all care activities.",
      "Follow biomedical waste segregation and disposal guidelines.",
    ],
    donts: [
      "Do not exceed your scope of competence or legal authority.",
      "Do not alter prescriptions or dosages without physician order.",
      "Do not falsify nursing records or omit critical observations.",
      "Do not reuse disposable syringes, needles, or sterile items.",
      "Do not disclose confidential patient information.",
      "Do not leave duty without proper clinical handover.",
      "Do not accept unauthorized payments or gifts for clinical favors.",
      "Avoid unprofessional conduct while on duty.",
    ],
    checkboxLabel: "I have read and understood my role, responsibilities, DO’s & DON’Ts.",
    continueLabel: "Agree & Continue",
  },
};

export function getCareRoleGuidelines(serviceRole) {
  const role = String(serviceRole || "").toLowerCase();
  if (role === "nurses" || role === "nurse") return CARE_ROLE_GUIDELINES.nurses;
  return CARE_ROLE_GUIDELINES.attendant;
}
